import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  CalendarBlank,
  Target,
  UsersThree,
  TreeStructure,
  ChartBar,
  UserCircle,
  CaretDown,
  IconProps,
} from '@phosphor-icons/react'

interface NavbarProps {
  isAdmin: boolean
  isGrowthWheelReviewer?: boolean
  isGrowthWheelEditor?: boolean
}

interface UserMenuProps {
  name: string
  userId: number
  isAdmin: boolean
  pictureUrl?: string
  onLogout: () => void
}

interface NavChild {
  label: string
  to: string
}

interface NavItem {
  key: string
  label: string
  icon: React.ComponentType<IconProps>
  to?: string
  children?: NavChild[]
  paths: string[]
}

interface MenuPos {
  top: number
  left?: number
  right?: number
}

const isPathActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(path + '/')

const Navbar = ({ isAdmin, isGrowthWheelReviewer, isGrowthWheelEditor }: NavbarProps) => {
  const location = useLocation()

  const showGrowthWheelDropdown = isGrowthWheelReviewer || isGrowthWheelEditor

  const items: NavItem[] = [
    {
      key: 'timeoff',
      label: 'Time Off',
      icon: CalendarBlank,
      children: [
        { label: 'Personal Time Off', to: '/time-off' },
        { label: 'Team Time Off', to: '/team-time-off' },
      ],
      paths: ['/time-off', '/team-time-off'],
    },
    showGrowthWheelDropdown
      ? {
          key: 'growthwheel',
          label: 'Growth Wheel',
          icon: Target,
          children: [
            { label: 'Contributor', to: '/growth-wheel' },
            ...(isGrowthWheelReviewer
              ? [{ label: 'Reviewer', to: '/growth-wheel-admin' }]
              : []),
            ...(isGrowthWheelEditor
              ? [{ label: 'Editor', to: '/edit-growth-wheel' }]
              : []),
          ],
          paths: ['/growth-wheel', '/growth-wheel-admin', '/edit-growth-wheel'],
        }
      : {
          key: 'growthwheel',
          label: 'Growth Wheel',
          icon: Target,
          to: '/growth-wheel',
          paths: ['/growth-wheel'],
        },
    {
      key: 'tribeorg',
      label: 'Tribe Org',
      icon: TreeStructure,
      to: '/tribeOrg',
      paths: ['/tribeOrg'],
    },
    ...(isAdmin
      ? [
          {
            key: 'team',
            label: 'Team Management',
            icon: UsersThree,
            children: [
              { label: 'Members', to: '/members' },
              { label: 'Engagements', to: '/engagements' },
              { label: 'Clients', to: '/clients' },
              { label: 'Tribes', to: '/tribes' },
              { label: 'Guilds', to: '/guilds' },
            ],
            paths: ['/members', '/engagements', '/clients', '/tribes', '/guilds'],
          },
          {
            key: 'reports',
            label: 'Reports',
            icon: ChartBar,
            children: [
              { label: 'Reports', to: '/reports' },
              { label: 'Release Plans', to: '/releaseplans' },
            ],
            paths: ['/reports', '/releaseplans'],
          },
        ]
      : []),
  ]

  return (
    <div className="global-nav">
      {items.map(item => {
        const isActive = item.paths.some(p => isPathActive(location.pathname, p))
        if (item.children) {
          return (
            <NavDropdown
              key={item.key}
              label={item.label}
              Icon={item.icon}
              isActive={isActive}
              items={item.children}
              pathname={location.pathname}
            />
          )
        }
        return (
          <Link
            key={item.key}
            to={item.to as string}
            className={`global-nav-pill ${isActive ? 'global-nav-pill--active' : ''}`}
          >
            <item.icon size={20} weight="regular" />
            {item.label}
          </Link>
        )
      })}
    </div>
  )
}

interface NavDropdownProps {
  label: string
  Icon: React.ComponentType<IconProps>
  isActive: boolean
  items: NavChild[]
  pathname: string
}

function NavDropdown({ label, Icon, isActive, items, pathname }: NavDropdownProps) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<MenuPos>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const openMenu = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 8, left: rect.left })
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        menuRef.current && !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="global-nav-dropdown">
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        className={`global-nav-pill ${isActive ? 'global-nav-pill--active' : ''}`}
      >
        <Icon size={20} weight="regular" />
        {label}
        <CaretDown size={14} weight="regular" />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="global-nav-dropdown__menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
        >
          {items.map(it => (
            <Link
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={`global-nav-dropdown__item ${
                isPathActive(pathname, it.to) ? 'global-nav-dropdown__item--active' : ''
              }`}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function UserMenu({ name, userId, isAdmin, pictureUrl, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<MenuPos>({ top: 0, right: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const openMenu = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        menuRef.current && !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="global-nav-dropdown global-user-menu">
      <button ref={triggerRef} type="button" onClick={openMenu} className="global-nav-pill">
        {pictureUrl ? (
          <img src={pictureUrl} alt={name} className="global-user-menu__avatar" />
        ) : (
          <UserCircle size={20} weight="regular" />
        )}
        {name}
        <CaretDown size={14} weight="regular" />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="global-nav-dropdown__menu"
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
        >
          {isAdmin && (
            <Link
              to={`/members/${userId}`}
              onClick={() => setOpen(false)}
              className="global-nav-dropdown__item"
            >
              Settings
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              onLogout()
              setOpen(false)
            }}
            className="global-nav-dropdown__item global-nav-dropdown__item--button"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  )
}

export default Navbar
